import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Profile, AppRole } from '@/types/database';
import { logAuditEvent } from '@/hooks/useAuditLog';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, role?: AppRole, tenantId?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isApprover: boolean;
  isVerifier: boolean;
  isObserver: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async (userId: string): Promise<Profile | null> => {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      return data as Profile | null;
    };

    // Listener for ONGOING auth changes (does NOT control loading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fire and forget for ongoing changes - profile already loaded on init
          fetchProfile(session.user.id).then(profileData => {
            if (isMounted) setProfile(profileData);
          });
        } else {
          setProfile(null);
        }
      }
    );

    // INITIAL load - controls loading state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // CRITICAL: Fetch profile BEFORE setting loading to false
        if (session?.user) {
          const profileData = await fetchProfile(session.user.id);
          if (isMounted) setProfile(profileData);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const getLoginGuardUrl = () => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    return `https://${projectId}.supabase.co/functions/v1/login-guard`;
  };

  const callLoginGuard = async (action: string, email: string, accessToken?: string, password?: string) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const response = await fetch(getLoginGuardUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(password ? { action, email, password } : { action, email }),
    });
    if (!response.ok) return null;
    return response.json();
  };

  const signIn = async (email: string, password: string) => {
    // Check if account is locked via server-side edge function
    const lockResult = await callLoginGuard('check', email, undefined, password);
    const lockCheck = lockResult?.result;

    if (lockCheck && lockCheck.length > 0 && lockCheck[0].is_locked) {
      const lockedUntil = new Date(lockCheck[0].locked_until);
      const minutesRemaining = Math.ceil((lockedUntil.getTime() - Date.now()) / 60000);
      
      await logAuditEvent({
        eventType: 'login_blocked',
        resourceType: 'auth',
        details: { 
          email, 
          reason: 'account_locked',
          attempts: lockCheck[0].attempts,
          locked_until: lockCheck[0].locked_until
        },
      });

      return { 
        error: new Error(`Conta bloqueada por excesso de tentativas. Tente novamente em ${minutesRemaining} minuto(s).`) 
      };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      // Record failed login attempt via server-side edge function
      const failedResponse = await callLoginGuard('record_failure', email, undefined, password);
      const failedResult = failedResponse?.result;

      const attempts = failedResult?.[0]?.attempts || 1;
      const isNowLocked = failedResult?.[0]?.is_now_locked || false;
      const lockedUntil = failedResult?.[0]?.locked_until;


      // Log failed login attempt
      await logAuditEvent({
        eventType: isNowLocked ? 'login_locked' : 'login_failed',
        resourceType: 'auth',
        details: { 
          email, 
          error: error.message,
          attempts,
          ...(isNowLocked && { locked_until: lockedUntil })
        },
      });

      if (isNowLocked) {
        const lockMinutes = attempts >= 10 
          ? 1440 
          : 15;
        const lockMessage = lockMinutes >= 1440
          ? `Conta bloqueada após ${attempts} tentativas falhas. Tente novamente em 24 horas.`
          : `Conta bloqueada após ${attempts} tentativas falhas. Tente novamente em 15 minutos.`;
        return { 
          error: new Error(lockMessage) 
        };
      }

      const remainingAttempts = 5 - attempts;
      if (remainingAttempts > 0 && remainingAttempts <= 3) {
        return { 
          error: new Error(`Credenciais inválidas. Restam ${remainingAttempts} tentativa(s) antes do bloqueio.`) 
        };
      }

      return { error };
    }

    // Check if user is active
    if (data.user) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('active')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) {
        await supabase.auth.signOut();
        return { error: new Error('Erro ao verificar status do usuário.') };
      }

      // If user is inactive, sign them out immediately
      if (profileData && profileData.active === false) {
        await logAuditEvent({
          eventType: 'login_failed',
          userId: data.user.id,
          resourceType: 'auth',
          details: { email, reason: 'user_inactive' },
        });
        await supabase.auth.signOut();
        return { error: new Error('Sua conta está desativada. Entre em contato com o administrador.') };
      }

      // Clear failed login attempts on successful login via server-side edge function
      // Log successful login using the session token from the login response
      // This ensures the token is available immediately, avoiding race conditions
      const accessToken = data.session?.access_token;
      await callLoginGuard('clear', email, accessToken || undefined);
      if (accessToken) {
        try {
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
          const response = await fetch(`https://${projectId}.supabase.co/functions/v1/log-audit-event`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({
              eventType: 'login_success',
              resourceType: 'auth',
              details: { email },
            }),
          });
          if (!response.ok) {
            console.error('Failed to log login audit:', await response.text());
          }
        } catch (error) {
          console.error('Failed to log login audit:', error);
        }
      }
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName: string, _role?: AppRole, _tenantId?: string) => {
    // SECURITY: Do NOT pass role or tenant_id in user metadata.
    // The handle_new_user trigger always forces 'observer' role.
    // Role elevation is handled server-side via the create-user edge function.
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Log logout before clearing state
    const currentUserId = user?.id;
    if (currentUserId) {
      await logAuditEvent({
        eventType: 'logout',
        userId: currentUserId,
        resourceType: 'auth',
      });
    }

    // Clear local state first to ensure UI updates
    setUser(null);
    setSession(null);
    setProfile(null);
    
    // Then attempt server-side logout (ignore errors if session already expired)
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Session might already be expired, which is fine
      console.log('Logout completed (session may have been expired)');
    }
  };

  const isSuperAdmin = profile?.role === 'super_admin';
  const isAdmin = profile?.role === 'admin' || isSuperAdmin;
  const isApprover = profile?.role === 'approver' || isAdmin;
  const isVerifier = profile?.role === 'verifier' || isApprover;
  const isObserver = profile?.role === 'observer' || isVerifier;
  const isConsultor = profile?.role === 'consultor';

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      isSuperAdmin,
      isAdmin,
      isApprover,
      isVerifier,
      isObserver,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
